import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Tag, Search, Mail, X } from 'lucide-react';
import { api } from '../../services/api';
import { User, UserRole } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AssignBrandsModal } from './AssignBrandsModal';

// Poradi roli pro razeni seznamu - shodne s poradim v ciselniku roli nize
// i ve formulari uzivatele. Neznama role skonci na konci.
const ROLE_ORDER: UserRole[] = ['admin', 'supplier', 'pre_approver', 'approver', 'end_user'];
const rolePoradi = (role: UserRole) => {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
};

const getRoleLabel = (role: UserRole): string => {
  const roleLabels: Record<UserRole, string> = {
    admin: 'Administrátor',
    supplier: 'Dodavatel',
    pre_approver: 'Předschvalovatel',
    approver: 'Schvalovatel',
    end_user: 'Prodejce',
  };
  return roleLabels[role] || role;
};

export const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  // ?role= z dlaždice na Přehledu admina (Dodavatelé, Schvalovatelé).
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFiltr = searchParams.get('role') as UserRole | null;
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get<User[]>('/users');
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    // Bez tohohle mazani selhavalo potichu - uzivatel s letaky nebo produkty
    // smazat nejde a hlaska ze serveru se nikde neukazala.
    onError: (e: any) => {
      alert(e?.response?.data?.message || 'Uživatele se nepodařilo smazat.');
    },
  });

  // Globální nastavení odesílání e-mailů (admin). Vypnutím se napříč aplikací
  // nepošle žádný e-mail (brána na backendu v EmailService.sendEmail).
  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => {
      const response = await api.get<{ emailNotificationsEnabled: boolean }>('/settings');
      return response.data;
    },
  });

  const emailToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await api.patch<{ emailNotificationsEnabled: boolean }>(
        '/settings/email-notifications',
        { enabled },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
    },
    onError: () => {
      alert('Nepodařilo se uložit nastavení odesílání e-mailů.');
    },
  });

  const emailEnabled = appSettings?.emailNotificationsEnabled ?? true;

  const handleDelete = async (user: User) => {
    if (window.confirm(`Opravdu chcete smazat uživatele "${user.firstName} ${user.lastName}"?`)) {
      // Chybu hlasi onError; tady jen at neskonci jako neodchycene odmitnuti.
      try {
        await deleteMutation.mutateAsync(user.id);
      } catch {
        /* ošetřeno v onError */
      }
    }
  };

  const handleAssignBrands = (user: User) => {
    setSelectedUser(user);
    setIsAssignModalOpen(true);
  };

  const handleAssignSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // Backend zadne razeni nedela, seznam by jinak chodil v poradi radku v tabulce.
  // Radime: aktivni napred (neaktivni az dole), pak role, zobrazovane jmeno a e-mail.
  const filteredUsers = users
    .filter((user) => !roleFiltr || user.role === roleFiltr)
    .filter((user) =>
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      const podleRole = rolePoradi(a.role) - rolePoradi(b.role);
      if (podleRole !== 0) return podleRole;
      const podleJmena = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'cs');
      if (podleJmena !== 0) return podleJmena;
      return a.email.localeCompare(b.email, 'cs');
    });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Uživatelé</h1>
          <p className="mt-2 text-gray-600">Spravujte uživatele systému</p>
        </div>
        <Button onClick={() => navigate('/admin/users/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nový uživatel
        </Button>
      </div>

      {/* Globální nastavení odesílání e-mailů (týká se všech uživatelů → nahoře na stránce Uživatelé) */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Odesílání e-mailů (celý systém)</h2>
              <p className="text-sm text-gray-600">
                Když je vypnuto, aplikace neodešle žádné e-mailové upozornění (odeslání letáku ke schválení,
                schválení, zamítnutí…). Nastavení platí globálně pro všechny uživatele.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Stav:{' '}
                <span className={emailEnabled ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                  {emailEnabled ? 'Zapnuto' : 'Vypnuto'}
                </span>
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={emailEnabled}
              disabled={emailToggleMutation.isPending || !appSettings}
              onChange={(e) => emailToggleMutation.mutate(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
          </label>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Hledat podle jména nebo emailu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        {roleFiltr && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Zobrazeni jen uživatelé s rolí</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-800 font-medium">
              {getRoleLabel(roleFiltr)}
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="hover:text-blue-900"
                title="Zrušit filtr role"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}
      </div>

      {filteredUsers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Plus className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádní uživatelé</h3>
          <p className="text-gray-600 mb-6">
            {search ? 'Zkuste upravit vyhledávání' : 'Začněte vytvořením prvního uživatele'}
          </p>
          {!search && (
            <Button onClick={() => navigate('/admin/users/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Vytvořit uživatele
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jméno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktivní
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Značky
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={`${user.firstName} ${user.lastName}`}>
                      {user.firstName} {user.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={user.email}>{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getRoleLabel(user.role)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Ano
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Ne
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.role === 'supplier' && user.brands && user.brands.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {user.brands.map((brand: any, index: number) => (
                          <span
                            key={index}
                            title={brand.name}
                            className="inline-block max-w-[10rem] truncate align-middle px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {brand.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50"
                        title="Upravit uživatele"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {user.role === 'supplier' && (
                        <button
                          onClick={() => handleAssignBrands(user)}
                          className="text-purple-600 hover:text-purple-900 p-2 rounded hover:bg-purple-50"
                          title="Přiřadit značky"
                        >
                          <Tag className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                        title="Smazat uživatele"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <AssignBrandsModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          user={selectedUser}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
};
